/**
 * Idle session policy for the New UI (NX-5238).
 *
 * Product-set and identical for every customer — the ticket is explicit that
 * this is not configurable per customer.
 *
 * The backend is what actually rejects an expired token; the values here decide
 * when the user is warned and when the client signs itself out. The backend's
 * WEB_SESSION_TTL_SECONDS is the hard cap on a token's life and must stay above
 * TOKEN_RENEW_BELOW_REMAINING_MS below, or renewal can never get far enough
 * ahead and the server starts refusing sessions the client still believes in.
 */

/**
 * Marks a request as coming from the New UI. The backend only enforces the idle
 * expiry on tokens minted for a request carrying this, so any call that mints a
 * token — login, the dual-system login, the reset-password exchange — has to
 * send it or the session it creates silently opts out of the timeout. Requests
 * that go through ApiConfig get it from the interceptor; the few that use raw
 * axios must set it themselves.
 *
 * Deliberately its own header rather than the existing `device` one: `device`
 * is the mobile app's contract and already decides something else on the
 * backend, so overloading it would tie two unrelated things together.
 */
export const PLATFORM_HEADER = 'X-Platform';
export const PLATFORM_WEB = 'web';

/**
 * Fallbacks, used until the server's policy arrives and if it never does.
 *
 * The server owns these values and serves them through /app-configs — see
 * resolveSessionPolicy below. They are duplicated here only so the timer has
 * something sane to run on during the first load of a brand new browser, before
 * any config has been fetched or cached.
 */
export const DEFAULT_SESSION_IDLE_MS = 30 * 60 * 1000;
export const DEFAULT_SESSION_WARNING_MS = 60 * 1000;

export type SessionPolicy = {
  /** Sign the user out after this long with no activity. */
  idleMs: number;
  /** Show the warning this long before that. */
  warningMs: number;
};

/** Shape of the `session` block served by /app-configs, all in seconds. */
export type ServedSessionPolicy = {
  idleSeconds?: unknown;
  warningSeconds?: unknown;
};

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Turns whatever /app-configs sent into a policy the timer can run on.
 *
 * Each field falls back independently, and a warning that is not shorter than
 * the window is rejected: a warning that begins the moment the user stops is
 * not a warning, and letting one through would mean the dialog is on screen for
 * the entire idle period.
 */
export function resolveSessionPolicy(
  served?: ServedSessionPolicy | null,
): SessionPolicy {
  const idleMs = isPositiveNumber(served?.idleSeconds)
    ? served.idleSeconds * 1000
    : DEFAULT_SESSION_IDLE_MS;

  const servedWarningMs = isPositiveNumber(served?.warningSeconds)
    ? served.warningSeconds * 1000
    : DEFAULT_SESSION_WARNING_MS;

  return {
    idleMs,
    warningMs:
      servedWarningMs < idleMs ? servedWarningMs : DEFAULT_SESSION_WARNING_MS,
  };
}

/**
 * Floor on how often a burst of activity is recorded. Pointer moves fire per
 * frame; writing each one to localStorage would be the most expensive thing on
 * the page.
 */
export const ACTIVITY_THROTTLE_MS = 1000;

/** How often the countdown re-reads the shared last-activity stamp. */
export const SESSION_TICK_MS = 1000;

/**
 * Renew the token once it can no longer cover a full fresh idle window.
 *
 * Keyed to the token's remaining life rather than to time since the last
 * renewal, because the idle window restarts on every action while the token
 * does not. Judging by elapsed time leaves a hole: renew at R, keep working
 * until just before the next renewal is due, then stop — the token dies well
 * before the client's window closes, and the user is signed out with no warning
 * at all, which is exactly what the ticket forbids.
 *
 * With this rule, any action leaves the token with at least a full window plus
 * the warning in hand, so the client always reaches its own deadline first and
 * the warning always gets shown. It holds because the server keeps its token
 * cap above idle + warning, which it clamps to enforce.
 */
export function renewBelowRemainingMs(policy: SessionPolicy): number {
  return policy.idleMs + policy.warningMs;
}

/** Floor between renewal requests, so a burst of activity cannot stack them. */
export const TOKEN_RENEW_MIN_GAP_MS = 30 * 1000;

/**
 * Shared across tabs: activity in any tab keeps every tab signed in, which is
 * why this lives in localStorage rather than in a per-tab timer. Storing the
 * moment of last activity rather than a countdown also means a tab whose timers
 * were throttled while backgrounded recomputes correctly on wake instead of
 * drifting.
 */
export const LAST_ACTIVITY_KEY = 'SESSION_LAST_ACTIVITY';

/**
 * The one message that means the session itself ended.
 *
 * Sent by the backend's JwtStrategy when it rejects an expired New UI token and
 * surfaced verbatim by its error filter. A 401 is only turned into a sign-out
 * when it says this, because most 401s mean something else entirely — a blocked
 * account, a login-as session taken over by the real user, a wrong password —
 * and those must not be reported to the user as thirty minutes of inactivity,
 * nor clear the storage every other tab is running on.
 *
 * Kept in step with `session.constants.ts` on the backend; the pairing is
 * covered by a test so a reword cannot quietly disable the redirect.
 */
export const SESSION_EXPIRED_MESSAGE = 'Session expired';

/** Set when a sign-out was caused by inactivity, so the login page can say so. */
export const SESSION_EXPIRED_KEY = 'SESSION_EXPIRED';

/**
 * DOM events treated as "the user is still there".
 *
 * All five bubble to window, which is what lets one listener cover the page.
 * `scroll` and `focus` are deliberately absent: neither bubbles, so reaching
 * them meant listening in the capture phase — which also delivers the ones the
 * app fires itself. The warning dialog focuses its own container as it opens,
 * so the dialog counted as activity, cleared itself, and the sign-out never
 * arrived.
 *
 * Nothing is lost by leaving them out, because every gesture that scrolls or
 * moves focus emits one of the five first: a scrollbar drag or a click is a
 * `mousedown`, a wheel or trackpad scroll is a `wheel`, Tab and the arrow keys
 * are a `keydown`, a swipe is a `touchstart`.
 */
export const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'wheel',
] as const;
